// Copyright (c) StrangeBee 2023

import React, { useState, ReactElement, useEffect } from 'react';
import { Store } from 'antd/lib/form/interface';
import { Form, FormInstance } from 'antd';
import { useLocalMessage, splitObservable, normalizeDate } from '@Helpers/';
import UploadSingleFile from '@Atoms/UploadSingleFile/UploadSingleFile';
import { SecurityInput } from '@Atoms/Inputs/SecurityInput/SecurityInput';
import { FormSubmitButton, FormSubmitOptions } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THTextArea } from '@Molecules/Inputs/THTextArea/THTextArea';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THMarkdown } from '@Molecules/Inputs/THMarkdown/THMarkdown';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { THAutoComplete } from '@Molecules/Inputs/THAutoComplete/THAutoComplete';
import { onOrganisationImageUpload } from '@Helpers/download';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { ObservableOutput, ObservableTypeOutput, PAPValueArray, TLPValueArray } from 'thehive-sdk';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';

import './ObservableForm.scss';

export interface ObservableValuesForm extends FormSubmitOptions {
    dataType: string;
    type: string;
    value: string;
    tlp: number;
    pap: number;
    description: string;
    ioc: boolean;
    sighted: boolean;
    sightedAt?: number;
    ignoreSimilarity: boolean;
    tags: string[];
    multiline: boolean;
    isZip: boolean;
    zipPassword: string;
    attachment: any;
}

interface ObservableFormProps {
    form: FormInstance;
    defaultValues?: ObservableOutput & { multiline?: boolean };
    isValid: boolean;
    onFinish(values: ObservableValuesForm): Promise<void>;
    onChange: () => void;
    onCancel: () => void;
}

export const ObservableForm = ({
    form,
    defaultValues,
    isValid,
    onFinish,
    onChange,
    onCancel,
}: ObservableFormProps): ReactElement | null => {
    const i = useLocalMessage();

    const { data: observableTypeList = [] } = useListQuery<ObservableTypeOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listObservableType' }],
                name: 'get-all-observable-types',
            },
            sort: ['+name'],
        },
        controller: client.ObservableTypeController,
    });

    const [stateLoading, setStateLoading] = useState(false);

    const [lineNumber, setLineNumber] = useState<number>(0);

    const countDataLines = (valueField = ''): void => {
        if (valueField) {
            const observableLines = splitObservable(valueField)?.length;
            setLineNumber(observableLines);
        } else {
            setLineNumber(0);
        }
    };

    useEffect(() => {
        if (defaultValues) {
            const { data = '' } = defaultValues;
            countDataLines(data.trim());
        }
    }, [defaultValues]);

    const onValuesChange = (changedValue: Store, allValues: ObservableValuesForm): void => {
        const { type, value } = allValues;

        const isAttachment = observableTypeList?.find((typeData) => type === typeData.name)?.isAttachment;

        if ('type' in changedValue) {
            countDataLines(value);
            form.resetFields(['attachment']);
        }

        if (!isAttachment) {
            countDataLines(value?.trim());
        }

        onChange();
    };

    const dataTypes = observableTypeList?.map(({ name }) => name).sort();
    const sighted = Form.useWatch(['sighted'], form);

    return (
        <THForm<ObservableValuesForm>
            name="observable-form"
            form={form}
            onFinish={(v) => {
                setStateLoading(true);
                onFinish(v).finally(() => setStateLoading(false));
            }}
            onValuesChange={onValuesChange}
            initialValues={{
                attachment: defaultValues?.attachment,
                type: defaultValues?.dataType,
                value: defaultValues?.data,
                description: defaultValues?.message,
                tags: defaultValues?.tags,
                tlp: 2,
                pap: 2,
                multiline: defaultValues?.multiline ?? true,
            }}
        >
            <THForm.Layout
                footer={
                    <FormSubmitButton
                        loading={stateLoading}
                        onCancel={onCancel}
                        disabled={!isValid}
                        onAnother={
                            !defaultValues
                                ? () => {
                                      form.setFieldsValue({
                                          another: true,
                                      });
                                      form.submit();
                                  }
                                : undefined
                        }
                        formName="observable-form"
                    />
                }
            >
                <THForm.Item
                    label={i('observable.create.form.type.label')}
                    name="type"
                    rules={[
                        {
                            required: true,
                            message: i('observable.create.form.type.required'),
                            validator: (_, value) => {
                                if (!value?.trim()) {
                                    return Promise.reject(i('observable.create.form.type.required'));
                                }

                                if (!dataTypes?.includes(value)) {
                                    return Promise.reject(i('observable.create.form.type.not-existing'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <THAutoComplete
                        filtered
                        options={dataTypes?.map((value) => ({ label: value, value }))}
                        placeholder={i('observable.create.form.type.placeholder')}
                    />
                </THForm.Item>

                <THForm.Item<ObservableValuesForm>
                    noStyle
                    shouldUpdate={(prevValues, newValues) =>
                        prevValues.type !== newValues.type ||
                        prevValues.isZip !== newValues.isZip ||
                        prevValues.zipPassword !== newValues.zipPassword ||
                        prevValues.multiline !== newValues.multiline ||
                        prevValues.value !== newValues.value
                    }
                >
                    {({ getFieldValue }) => {
                        const isAttachment = observableTypeList?.find(
                            (type) => getFieldValue('type') === type.name,
                        )?.isAttachment;

                        const lineNumberProcessed =
                            getFieldValue('multiline') === true ? lineNumber : Math.sign(lineNumber);

                        return (
                            <>
                                <THForm.Item
                                    extraAction={
                                        <>
                                            {i('observable.create.form.attachment.is-zip.label')}
                                            <THForm.Item name="isZip" valuePropName="checked" noStyle>
                                                <THSwitch />
                                            </THForm.Item>
                                        </>
                                    }
                                    label={i('observable.create.form.attachment.label')}
                                    name="attachment"
                                    rules={[
                                        {
                                            required: isAttachment,
                                            message: i('observable.create.form.attachment.required'),
                                        },
                                    ]}
                                    hidden={!isAttachment}
                                >
                                    <UploadSingleFile text={i('observable.create.form.attachment.placeholder')} />
                                </THForm.Item>

                                <THForm.Item
                                    label={i('observable.create.form.attachment.zip-password.label')}
                                    name="zipPassword"
                                    hidden={!isAttachment || !getFieldValue('isZip')}
                                >
                                    <THInput
                                        placeholder={i('observable.create.form.attachment.zip-password.placeholder')}
                                    />
                                </THForm.Item>

                                <THForm.Item
                                    extraAction={
                                        <>
                                            {i('observable.create.form.value.multiline.unique.label')}
                                            <THForm.Item name="multiline" valuePropName="checked" noStyle>
                                                <THSwitch />
                                            </THForm.Item>
                                            {`${lineNumberProcessed} ${i(
                                                'observable.create.form.value.multiline.unique.observable',
                                            )}(s)`}
                                        </>
                                    }
                                    label={i('observable.create.form.value.label')}
                                    name="value"
                                    rules={[
                                        {
                                            required: !isAttachment,
                                            message: i('observable.create.form.value.required'),
                                        },
                                    ]}
                                    className="observable-form__value"
                                    hidden={isAttachment}
                                >
                                    <THTextArea placeholder={i('observable.create.form.value.placeholder')} />
                                </THForm.Item>
                            </>
                        );
                    }}
                </THForm.Item>

                <THForm.Item name="tlp" label={i('common.tlp')} className="observable-form__options__security">
                    <SecurityInput type="tlp" valueArray={TLPValueArray} />
                </THForm.Item>

                <THForm.Item name="pap" label={i('common.pap')} className="observable-form__options__security">
                    <SecurityInput type="pap" valueArray={PAPValueArray} />
                </THForm.Item>

                <div className="observable-form__options">
                    <THForm.Item
                        name="ioc"
                        label={i('observable.create.form.ioc.label')}
                        valuePropName="checked"
                        className="observable-form__options__other"
                    >
                        <THSwitch />
                    </THForm.Item>

                    <THForm.Item
                        name="sighted"
                        label={i('observable.create.form.sighted.label')}
                        valuePropName="checked"
                        className="observable-form__options__other"
                    >
                        <THSwitch />
                    </THForm.Item>

                    <THForm.Item
                        name="ignoreSimilarity"
                        label={i('observable.create.form.similarity.label')}
                        valuePropName="checked"
                        className="observable-form__options__other"
                    >
                        <THSwitch />
                    </THForm.Item>
                </div>

                <THForm.Item
                    name="sightedAt"
                    hidden={!sighted}
                    label={i('observable.create.form.sightedAt.label')}
                    rules={[{ required: sighted, message: i('observable.create.form.sightedAt.required') }]}
                    normalize={normalizeDate}
                >
                    <THDatePicker />
                </THForm.Item>

                <THForm.Item name="tags" label={i('observable.create.form.tags.label')}>
                    <THTagSelector.Autocomplete placeholder={i('common.tags')} />
                </THForm.Item>

                <THForm.Item name="description" label={i('observable.create.form.description.label')}>
                    <THMarkdown sameSize onImageUpload={onOrganisationImageUpload} />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
